import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ArrowRight, GraduationCap, UtensilsCrossed, Home as HomeIcon, HeartHandshake, Stethoscope, Sunrise, Sparkles, Users, HandHeart } from "lucide-react";
import { motion } from "framer-motion";
import { useI18n } from "../context/I18nContext";
import { api } from "../lib/api";
import CountUp from "../components/CountUp";
import MonthlyDonationsCounter from "../components/MonthlyDonationsCounter";

const HERO_BG = "https://images.unsplash.com/photo-1679639679185-146e2e6f7875?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxhZnJpY2FuJTIwbGFuZHNjYXBlJTIwd2FybSUyMHN1bnJpc2UlMjBibHVyfGVufDB8fHx8MTc4NjIzMzE2N3ww&ixlib=rb-4.1.0&q=85";
const PILOT_BG = "https://images.unsplash.com/photo-1507005821228-0997492426e0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxwZW9wbGUlMjBob2xkaW5nJTIwaGFuZHMlMjBzaWxob3VldHRlJTIwc3Vuc2V0fGVufDB8fHx8MTc4NjIzMzE3Nnww&ixlib=rb-4.1.0&q=85";

// Motion configs — extracted to constants to avoid creating new object references on each render
const FADE_IN_UP = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7 } };
const FADE_IN_RIGHT = { initial: { opacity: 0, x: 30 }, animate: { opacity: 1, x: 0 }, transition: { delay: 0.3, duration: 0.8 } };
const DOMAIN_INITIAL = { opacity: 0, y: 20 };
const DOMAIN_WHILE_IN_VIEW = { opacity: 1, y: 0 };
const DOMAIN_VIEWPORT = { once: true };

const domains = [
  { icon: GraduationCap, tag: "01", title: "Éducation", desc: "Soutenir la scolarisation, fournir du matériel et lever les obstacles économiques.", span: "lg:col-span-5 lg:row-span-2", tone: "bg-deep-blue text-ivory" },
  { icon: UtensilsCrossed, tag: "02", title: "Alimentation", desc: "Repas, paniers et actions alimentaires pour les enfants et familles vulnérables.", span: "lg:col-span-4", tone: "bg-ivory border border-black/5" },
  { icon: HomeIcon, tag: "03", title: "Protection", desc: "Environnements dignes et sécurisés, en lien avec les structures compétentes.", span: "lg:col-span-3", tone: "bg-warm-orange text-ivory" },
  { icon: HeartHandshake, tag: "04", title: "Accompagnement", desc: "Bien-être émotionnel et psychosocial : écoute, présence, activités.", span: "lg:col-span-4", tone: "bg-green-soft text-ivory" },
  { icon: Stethoscope, tag: "05", title: "Santé", desc: "Accès aux soins et actions de prévention selon les ressources disponibles.", span: "lg:col-span-3", tone: "bg-ivory border border-black/5" },
  { icon: Sunrise, tag: "06", title: "Autonomie & avenir", desc: "Compétences, formation, emploi, entrepreneuriat pour les jeunes.", span: "lg:col-span-5", tone: "bg-gold text-deep-blue" },
];

export default function Home() {
  const { t } = useI18n();
  const [pilot, setPilot] = useState({ target_children: 100, current_children: 0, kits_distributed: 0, families_reached: 0, funds_collected: 0 });
  const [site, setSite] = useState({});

  useEffect(() => {
    const load = () => api.get("/settings").then(r => { if (r.data?.pilot) setPilot(r.data.pilot); setSite(r.data?.site || {}); }).catch(() => {});
    load();
    window.addEventListener("lel-settings-updated", load);
    return () => window.removeEventListener("lel-settings-updated", load);
  }, []);
  const heroBg = site.hero_image_url || HERO_BG;

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] overflow-hidden">
        <div className="absolute inset-0">
          {site.hero_video_url ? (
            <video
              key={site.hero_video_url}
              autoPlay
              muted
              loop
              playsInline
              poster={site.hero_image_url || HERO_BG}
              className="w-full h-full object-cover"
              data-testid="hero-video"
            >
              <source src={site.hero_video_url} />
            </video>
          ) : (
            <img src={heroBg} alt="Aube d'espoir" className="w-full h-full object-cover" data-testid="hero-image"/>
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-deep-blue/95 via-deep-blue/80 to-deep-blue/40"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-deep-blue/70 to-transparent"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 pt-24 pb-20 grid lg:grid-cols-12 gap-10 items-center min-h-[80vh]">
          <div className="lg:col-span-8">
            <motion.div {...FADE_IN_UP}>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gold/15 text-gold border border-gold/30 mb-8">
                <Sparkles size={14} /> <span className="text-xs uppercase tracking-[0.25em] font-semibold">{t("hero.tagline")}</span>
              </div>
              <h1 className="font-display text-ivory text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[1.02] tracking-tight" data-testid="hero-title">
                {t("hero.brand").split(" ").map((w, i) => (
                  <span key={`brand-${w}-${i}`} className={i === 2 ? "text-gold italic" : ""}>{w} </span>
                ))}
              </h1>
              <p className="font-display italic text-gold/90 text-2xl sm:text-3xl mt-6 max-w-2xl">« {t("hero.slogan") } »</p>
              <p className="text-ivory/80 mt-6 max-w-xl text-lg leading-relaxed">{t("hero.body")}</p>
              <div className="flex flex-wrap gap-4 mt-10">
                <Link to="/faire-un-don" className="btn-gold px-8 py-4 rounded-full font-semibold text-sm inline-flex items-center gap-2" data-testid="hero-donate-btn">
                  <Heart size={18} className="fill-deep-blue"/> {t("hero.donate")}
                </Link>
                <Link to="/soutenir" className="px-8 py-4 rounded-full font-semibold text-sm border-2 border-ivory/40 text-ivory hover:bg-ivory hover:text-deep-blue transition inline-flex items-center gap-2" data-testid="hero-support-btn">
                  <HandHeart size={18}/> {t("hero.support")}
                </Link>
                <Link to="/devenir-benevole" className="px-8 py-4 rounded-full font-semibold text-sm text-gold hover:text-warm-orange transition inline-flex items-center gap-2" data-testid="hero-volunteer-btn">
                  <Users size={18}/> {t("hero.volunteer")} <ArrowRight size={16}/>
                </Link>
              </div>
            </motion.div>
          </div>
          <div className="hidden lg:block lg:col-span-4">
            <motion.div {...FADE_IN_RIGHT} className="backdrop-blur-md bg-white/5 border border-white/10 rounded-2xl p-8">
              <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">{t("hero.foundingLabel")}</div>
              <p className="font-display italic text-ivory text-2xl leading-relaxed">« {t("hero.founding")} »</p>
              <div className="mt-6 h-px bg-gradient-to-r from-gold to-transparent" />
              <div className="mt-6 flex items-center gap-3">
                <div className="w-1 h-12 bg-gold" />
                <div>
                  <div className="text-ivory/70 text-xs uppercase tracking-widest">{t("hero.commitmentLabel")}</div>
                  <div className="text-ivory font-medium">{t("hero.commitmentValue")}</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="relative py-24 hero-gradient" data-testid="section-mission">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="text-xs uppercase tracking-[0.3em] text-warm-orange font-semibold">{t("mission.tag")}</div>
            <h2 className="font-display text-deep-blue text-5xl sm:text-6xl mt-3 leading-[1.05]">
              {t("mission.h1")}<br/><span className="text-warm-orange italic">{t("mission.h2")}</span>
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6">
            <p className="text-deep-blue/90 text-lg leading-relaxed">{t("mission.p1")}</p>
            <p className="text-deep-blue/70 text-base leading-relaxed mt-6">{t("mission.p2")}</p>
            <Link to="/a-propos" className="btn-ghost-blue mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold" data-testid="mission-cta">
              {t("mission.cta")} <ArrowRight size={16}/>
            </Link>
          </div>
        </div>
      </section>

      {/* MONTHLY DONATIONS COUNTER */}
      <MonthlyDonationsCounter />

      {/* DOMAINS - BENTO */}
      <section className="py-24 bg-pearl" data-testid="section-domains">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12 gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-warm-orange font-semibold">{t("domains.tag")}</div>
              <h2 className="font-display text-deep-blue text-5xl sm:text-6xl mt-3">{t("domains.title")}</h2>
            </div>
            <p className="text-deep-blue/60 max-w-md">{t("domains.sub")}</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 auto-rows-[210px]">
            {domains.map((d, i) => {
              const Icon = d.icon;
              return (
                <motion.div key={d.title} initial={DOMAIN_INITIAL} whileInView={DOMAIN_WHILE_IN_VIEW} viewport={DOMAIN_VIEWPORT} transition={{ delay: i * 0.08 }}
                  className={`${d.span} ${d.tone} rounded-2xl p-8 flex flex-col justify-between overflow-hidden relative group`}
                  data-testid={`domain-${i}`}>
                  <div className="flex items-start justify-between">
                    <Icon size={40} className="opacity-90"/>
                    <span className="font-display text-4xl opacity-30">{d.tag}</span>
                  </div>
                  <div>
                    <h3 className="font-display text-2xl sm:text-3xl">{d.title}</h3>
                    <p className="text-sm opacity-80 mt-2 leading-relaxed">{d.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PILOT PROJECT */}
      <section className="relative py-24 overflow-hidden deep-section" data-testid="section-pilot">
        <div className="absolute inset-0 opacity-20">
          <img src={PILOT_BG} alt="Silhouettes" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">{t("pilot.tag")}</div>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-4 leading-[1.05]">{t("pilot.t1")}<br/><span className="text-gold italic">{t("pilot.t2")}</span></h2>
            <p className="text-ivory/70 mt-6 text-lg max-w-2xl">{t("pilot.desc")}</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-14">
            {[
              { l: t("pilot.s1"), v: pilot.target_children, s: t("pilot.u1") },
              { l: t("pilot.s2"), v: pilot.current_children, s: t("pilot.u2") },
              { l: t("pilot.s3"), v: pilot.kits_distributed, s: t("pilot.u3") },
              { l: t("pilot.s4"), v: pilot.funds_collected, s: t("pilot.u4") },
            ].map((c, i) => (
              <div key={c.s} className="p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur" data-testid={`pilot-stat-${i}`}>
                <div className="font-display text-5xl text-gold"><CountUp value={c.v} /></div>
                <div className="text-xs uppercase tracking-widest text-ivory/70 mt-2">{c.s}</div>
                <div className="text-ivory mt-1">{c.l}</div>
              </div>
            ))}
          </div>
          <Link to="/faire-un-don" className="btn-gold mt-10 inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-sm" data-testid="pilot-cta">
            {t("pilot.cta")} <ArrowRight size={16}/>
          </Link>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="py-20 bg-ivory">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid md:grid-cols-3 gap-6">
          <Link to="/faire-un-don" className="card-elev rounded-2xl p-8 group" data-testid="cta-donate">
            <Heart className="text-warm-orange" size={32}/>
            <h3 className="font-display text-2xl mt-4 text-deep-blue">{t("homeCta.donateT")}</h3>
            <p className="text-deep-blue/60 mt-2 text-sm">{t("homeCta.donateD")}</p>
            <span className="inline-flex items-center gap-2 text-gold font-semibold mt-6 text-sm group-hover:gap-3 transition-all">{t("homeCta.donateBtn")} <ArrowRight size={14}/></span>
          </Link>
          <Link to="/devenir-benevole" className="card-elev rounded-2xl p-8 group" data-testid="cta-volunteer">
            <Users className="text-green-soft" size={32}/>
            <h3 className="font-display text-2xl mt-4 text-deep-blue">{t("homeCta.volT")}</h3>
            <p className="text-deep-blue/60 mt-2 text-sm">{t("homeCta.volD")}</p>
            <span className="inline-flex items-center gap-2 text-gold font-semibold mt-6 text-sm group-hover:gap-3 transition-all">{t("homeCta.volBtn")} <ArrowRight size={14}/></span>
          </Link>
          <Link to="/entreprises-partenaires" className="card-elev rounded-2xl p-8 group" data-testid="cta-partner">
            <HandHeart className="text-deep-blue" size={32}/>
            <h3 className="font-display text-2xl mt-4 text-deep-blue">{t("homeCta.partT")}</h3>
            <p className="text-deep-blue/60 mt-2 text-sm">{t("homeCta.partD")}</p>
            <span className="inline-flex items-center gap-2 text-gold font-semibold mt-6 text-sm group-hover:gap-3 transition-all">{t("homeCta.partBtn")} <ArrowRight size={14}/></span>
          </Link>
        </div>
      </section>
    </div>
  );
}
